<template>
  <section class="px-3 pb-3">
    <AdminToastStack :toasts="toasts" @close="removeToast" />

    <div class="admin-surface">
      <div class="admin-surface__head">
        <span class="admin-surface__title">Статистика</span>
        <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadStatistics">
          <i class="bi bi-arrow-repeat me-1"></i>
          Обновить
        </button>
      </div>

      <div class="row g-2 mt-1">
        <div class="col-12 col-md-2">
          <label class="form-label mb-1 small text-muted">fromDate</label>
          <input v-model="filters.fromDate" type="date" class="form-control form-control-sm" />
        </div>
        <div class="col-12 col-md-2">
          <label class="form-label mb-1 small text-muted">toDate</label>
          <input v-model="filters.toDate" type="date" class="form-control form-control-sm" />
        </div>
        <div class="col-12 col-md-2">
          <label class="form-label mb-1 small text-muted">userId (опционально)</label>
          <input v-model.trim="filters.userId" min="1" type="number" class="form-control form-control-sm" placeholder="Все пользователи" />
        </div>
        <div class="col-12 col-md-3">
          <label class="form-label mb-1 small text-muted">sortBy</label>
          <select v-model="filters.sortBy" class="form-select form-select-sm">
            <option v-for="option in SORT_BY_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div class="col-6 col-md-1">
          <label class="form-label mb-1 small text-muted">dir</label>
          <select v-model="filters.sortDir" class="form-select form-select-sm">
            <option value="desc">desc</option>
            <option value="asc">asc</option>
          </select>
        </div>
        <div class="col-3 col-md-1 d-grid align-content-end">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="loadStatistics">OK</button>
        </div>
        <div class="col-3 col-md-1 d-grid align-content-end">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="resetFilters">Сброс</button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 px-3 mt-3 mb-0">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2 px-3 mt-3 mb-0">{{ successMessage }}</div>

      <div v-if="statistics" class="mt-3">
        <div class="admin-period text-muted small mb-2">
          Период: {{ statistics.fromDate || '—' }} - {{ statistics.toDate || '—' }} | userId filter: {{ statistics.userIdFilter || '—' }}
          | sort: {{ filters.sortBy }} {{ filters.sortDir }}
        </div>

        <div class="admin-stats-grid">
          <article class="admin-stat-card">
            <div class="admin-stat-card__label">Users Total</div>
            <div class="admin-stat-card__value">{{ statistics.system.usersTotal }}</div>
          </article>
          <article class="admin-stat-card">
            <div class="admin-stat-card__label">Users Enabled</div>
            <div class="admin-stat-card__value">{{ statistics.system.usersEnabled }}</div>
          </article>
          <article class="admin-stat-card">
            <div class="admin-stat-card__label">Streams Total</div>
            <div class="admin-stat-card__value">{{ statistics.system.streamsTotal }}</div>
          </article>
          <article class="admin-stat-card">
            <div class="admin-stat-card__label">Contents Total</div>
            <div class="admin-stat-card__value">{{ statistics.system.contentsTotal }}</div>
          </article>
          <article class="admin-stat-card">
            <div class="admin-stat-card__label">Published Contents</div>
            <div class="admin-stat-card__value">{{ statistics.system.publishedContentsCount }}</div>
          </article>
          <article class="admin-stat-card">
            <div class="admin-stat-card__label">AI Requests</div>
            <div class="admin-stat-card__value">{{ statistics.system.aiRequestsCount }}</div>
          </article>
          <article class="admin-stat-card">
            <div class="admin-stat-card__label">Стоимость всего ($)</div>
            <div class="admin-stat-card__value">{{ usd(statistics.system.costUsd) }}</div>
          </article>
          <article class="admin-stat-card">
            <div class="admin-stat-card__label">Стоимость LLM ($)</div>
            <div class="admin-stat-card__value">{{ usd(statistics.system.directLlmUsd) }}</div>
          </article>
          <article class="admin-stat-card">
            <div class="admin-stat-card__label">Поиск картинок ($)</div>
            <div class="admin-stat-card__value">{{ usd(statistics.system.imageSearchUsd) }}</div>
          </article>
          <article class="admin-stat-card">
            <div class="admin-stat-card__label">cmaker ($)</div>
            <div class="admin-stat-card__value">{{ usd(statistics.system.cmakerUsd) }}</div>
          </article>
        </div>

        <div class="table-responsive admin-table-wrap mt-3">
          <table class="table table-sm align-middle mb-0 admin-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Username</th>
                <th>Enabled</th>
                <th>Streams</th>
                <th>Contents</th>
                <th>Published</th>
                <th>AI Req</th>
                <th>Стоимость ($)</th>
                <th>LLM ($)</th>
                <th>Картинки ($)</th>
                <th>cmaker ($)</th>
              </tr>
            </thead>
            <tbody v-if="statistics.users.length">
              <tr v-for="user in statistics.users" :key="user.userId">
                <td>{{ user.userId }}</td>
                <td>{{ user.username }}</td>
                <td>
                  <span :class="user.enabled ? 'text-success' : 'text-danger'">
                    {{ user.enabled ? 'Да' : 'Нет' }}
                  </span>
                </td>
                <td>{{ user.streamsTotal }}</td>
                <td>{{ user.contentsTotal }}</td>
                <td>{{ user.publishedContentsCount }}</td>
                <td>{{ user.aiRequestsCount }}</td>
                <td>{{ usd(user.costUsd) }}</td>
                <td>{{ usd(user.directLlmUsd) }}</td>
                <td>{{ usd(user.imageSearchUsd) }}</td>
                <td>{{ usd(user.cmakerUsd) }}</td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="11" class="text-center text-muted py-3">
                  {{ isLoading ? 'Загрузка...' : 'Нет данных по пользователям за выбранный период.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="admin-empty-state mt-3">
        {{ isLoading ? 'Загрузка статистики...' : 'Статистика не загружена. Примените фильтры и выполните запрос.' }}
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import AdminToastStack from '@/admin/components/AdminToastStack.vue'
import { useAdminRequestState } from '@/admin/composables/useAdminRequestState'
import { getAdminStatistics } from '@/admin/api/adminService'

const SORT_BY_OPTIONS = Object.freeze([
  { value: 'userId', label: 'userId' },
  { value: 'username', label: 'username' },
  { value: 'streamsTotal', label: 'streamsTotal' },
  { value: 'contentsTotal', label: 'contentsTotal' },
  { value: 'publishedContentsCount', label: 'publishedContentsCount' },
  { value: 'costUsd', label: 'costUsd' },
])

const usdFormatter = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'USD' })
const usd = value => usdFormatter.format(Number(value || 0))

const getTodayDate = () => {
  const date = new Date()
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

const getFirstDateOfCurrentMonth = () => {
  const date = new Date()
  date.setDate(1)
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

const filters = reactive({
  fromDate: getFirstDateOfCurrentMonth(),
  toDate: getTodayDate(),
  userId: '',
  sortBy: 'userId',
  sortDir: 'desc',
})

const statistics = ref(null)

const {
  isLoading,
  errorMessage,
  successMessage,
  toasts,
  removeToast,
  clearMessages,
  execute,
} = useAdminRequestState()

const loadStatistics = async () => {
  clearMessages()
  const response = await execute(
    () =>
      getAdminStatistics({
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        userId: filters.userId,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir,
      }),
    {
      error: 'Не удалось загрузить статистику.',
    }
  )
  statistics.value = response
}

const resetFilters = async () => {
  filters.fromDate = getFirstDateOfCurrentMonth()
  filters.toDate = getTodayDate()
  filters.userId = ''
  filters.sortBy = 'userId'
  filters.sortDir = 'desc'
  await loadStatistics()
}

onMounted(async () => {
  await loadStatistics()
})
</script>

<style scoped>
.admin-surface {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.admin-surface__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-surface__title {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
}

.admin-period {
  padding: 4px 2px;
}

.admin-stats-grid {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.admin-stat-card {
  flex: 1 1 0;
  min-width: 90px;
  border: 1px solid #dce7fa;
  border-radius: 8px;
  background: #f8fbff;
  padding: 6px 8px;
}

.admin-stat-card__label {
  color: #556989;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.admin-stat-card__value {
  color: #1f2d45;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
}

.admin-table-wrap {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  max-height: 72vh;
  overflow: auto;
}

.admin-table thead th {
  position: sticky;
  top: 0;
  background: #f8fbff;
  z-index: 2;
  color: #607089;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e9edf6;
}

.admin-empty-state {
  border: 1px dashed #d6e3f8;
  border-radius: 12px;
  padding: 18px;
  color: #5f6f89;
  background: #fcfdff;
}
</style>
